/**
 * Country Visas Page
 * Displays all visa types for a specific country - fetched dynamically from DB
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCountryComplete, formatCurrency, formatDuration, getCategoryDisplayName } from '../../utils/visaApi';
import type { CountryWithVisas } from '../../types/visa';
import './CountryVisasPage.css';

export const CountryVisasPage: React.FC = () => {
  const { countryCode: iso2 } = useParams<{ countryCode: string }>();
  const navigate = useNavigate();
  const [country, setCountry] = useState<CountryWithVisas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    if (iso2) {
      loadCountryData(iso2);
    }
  }, [iso2]);

  const loadCountryData = async (countryIso2: string) => {
    try {
      setLoading(true);
      const data = await fetchCountryComplete(countryIso2);
      setCountry(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="country-visas-page loading">
        <div className="spinner"></div>
        <p>Loading visa information...</p>
      </div>
    );
  }

  if (error || !country) {
    return (
      <div className="country-visas-page error">
        <h2>Error Loading Country Data</h2>
        <p>{error || 'Country not found'}</p>
        <button onClick={() => navigate('/countries')} className="back-link">← Back to Countries</button>
      </div>
    );
  }

  // Get unique categories
  const categories = Array.from(new Set(country.VisaType.map(v => v.category)));
  const filteredVisas = selectedCategory === 'all'
    ? country.VisaType
    : country.VisaType.filter(v => v.category === selectedCategory);

  return (
    <div className="country-visas-page">
      <header className="country-header">
        <div className="country-title">
          <span className="country-flag-large">
            {String.fromCodePoint(...Array.from(country.iso2).map(c => 127397 + c.charCodeAt(0)))}
          </span>
          <div>
            <h1>{country.name}</h1>
            <p className="country-meta">{country.region} · {country.VisaType.length} Visa Types</p>
          </div>
        </div>
        <button onClick={() => navigate('/countries')} className="back-link">← All Countries</button>
      </header>

      <div className="category-filter">
        <button
          className={selectedCategory === 'all' ? 'active' : ''}
          onClick={() => setSelectedCategory('all')}
        >
          All ({country.VisaType.length})
        </button>
        {categories.map((category) => {
          const count = country.VisaType.filter(v => v.category === category).length;
          return (
            <button
              key={category}
              className={selectedCategory === category ? 'active' : ''}
              onClick={() => setSelectedCategory(category)}
            >
              {getCategoryDisplayName(category)} ({count})
            </button>
          );
        })}
      </div>

      <div className="visas-grid">
        {filteredVisas.map((visa) => (
          <div key={visa.id} className="visa-card">
            <div className="visa-header">
              <h3>{visa.name}</h3>
              <span className={`visa-badge ${visa.category}`}>
                {getCategoryDisplayName(visa.category)}
              </span>
            </div>
            
            <p className="visa-description">{visa.description || 'No description available'}</p>

            <div className="visa-details">
              {visa.feeAmount && (
                <div className="detail-item">
                  <span className="detail-label">Fee:</span>
                  <span className="detail-value">
                    {formatCurrency(visa.feeAmount, visa.feeCurrency || 'USD')}
                  </span>
                </div>
              )}
              
              {visa.validityDays && (
                <div className="detail-item">
                  <span className="detail-label">Validity:</span>
                  <span className="detail-value">{formatDuration(visa.validityDays)}</span>
                </div>
              )}
              
              {visa.stayDays && (
                <div className="detail-item">
                  <span className="detail-label">Max Stay:</span>
                  <span className="detail-value">{formatDuration(visa.stayDays)}</span>
                </div>
              )}
              
              {visa.processingAvgDays && (
                <div className="detail-item">
                  <span className="detail-label">Processing:</span>
                  <span className="detail-value">{visa.processingAvgDays} days</span>
                </div>
              )}
            </div>

            <div className="visa-features">
              {visa.online && <span className="feature-badge">📱 Online Application</span>}
              {visa.biometrics && <span className="feature-badge">👆 Biometrics Required</span>}
              {visa.multipleEntry && <span className="feature-badge">🔄 Multiple Entry</span>}
              {visa.allowsWork && <span className="feature-badge">💼 Work Permitted</span>}
              {visa.allowsStudy && <span className="feature-badge">📚 Study Permitted</span>}
            </div>

            <button onClick={() => navigate(`/countries/${iso2}/visas/${visa.id}`)} className="view-details-btn">
              View Full Details →
            </button>
          </div>
        ))}
      </div>

      {filteredVisas.length === 0 && (
        <div className="no-visas">
          <p>No visas found in this category.</p>
        </div>
      )}

      {country.Consulate && country.Consulate.length > 0 && (
        <div className="consulates-section">
          <h2>Consulates & Embassies</h2>
          <div className="consulates-list">
            {country.Consulate.map((consulate) => (
              <div key={consulate.id} className="consulate-card">
                <h4>{consulate.name}</h4>
                <p>{consulate.city}</p>
                {consulate.website && (
                  <a href={consulate.website} target="_blank" rel="noopener noreferrer">
                    Visit Website →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryVisasPage;